import { useQuery } from '@apollo/client';
import { Center, FlexProps, Spinner } from '@chakra-ui/react';
import { transactionSchedulesFromGoalAccount } from '@common/financialEntities';
import { FinancialEntitySnapshotsDocument } from '@common/graphql/generated';
import { getGoalAccountBalance, getGoalAccountVSG } from '@common/react/goals';
import { dt } from '@common/util/dateTime';
import { FC, useMemo } from 'react';
import { FlexCol } from '../common/Flex';
import { configByGoalType } from './config';
import { useGoalAccountContext } from './GoalAccountDetailContainer';
import { useGoalDetailChartRangeControls } from './goalChartHelper';
import { GoalsChartEntityParams } from './GoalsChart';
import GoalsChartContainer from './GoalsChartContainer';

export interface GoalAccountDetailChartViewProps extends FlexProps {}

const GoalAccountDetailChartView: FC<GoalAccountDetailChartViewProps> = ({
    ...props
}) => {
    const { goal, goalAccount, goalAccountName, annualReturn } =
        useGoalAccountContext();
    const transactionSchedules =
        transactionSchedulesFromGoalAccount(goalAccount);

    const { data: snapshotsData, loading: isSnapshotsLoading } = useQuery(
        FinancialEntitySnapshotsDocument,
        {
            variables: { id: goalAccount.id },
            fetchPolicy: 'no-cache',
        }
    );
    const snapshots = useMemo(
        () => snapshotsData?.financialEntity?.snapshots ?? [],
        [snapshotsData?.financialEntity?.snapshots]
    );

    const goalConfig = configByGoalType[goal.type];
    const color = goalConfig.color;
    const goalAccountBalance = getGoalAccountBalance(goalAccount);
    const currentDate = dt().toDateStr();

    const { chartRangeType, range, onIncrement, onDecrement, onChangeType } =
        useGoalDetailChartRangeControls({ accounts: [goalAccount] });

    const vsg = useMemo(() => {
        const valueNodeBaseParams = {
            goalId: goal.id,
            name: goalAccountName,
            color,
        };

        return getGoalAccountVSG({
            owner: {
                annualReturn,
                currentValue: goalAccountBalance ?? 0,
                snapshots,
                transactionSchedulePlans: transactionSchedules,
            },
            opts: {},
            currentDate,
            valueNodeBaseParams,
            // TODO: Consider making this an optional prop to avoid this
            maybeCapBalance: (_, balance: number) => balance,
        });
    }, [
        goal.id,
        goalAccountName,
        color,
        annualReturn,
        goalAccountBalance,
        snapshots,
        transactionSchedules,
        currentDate,
    ]);

    const chartEntities = useMemo(() => {
        const valueSeries = vsg(range);

        const entity: GoalsChartEntityParams = {
            id: goal.id,
            valueSeries,
            color,
        };

        return [entity];
    }, [color, goal.id, range, vsg]);

    return (
        <FlexCol shrink={0} grow={0} {...props}>
            {isSnapshotsLoading ? (
                <Center w="full">
                    <Spinner />
                </Center>
            ) : (
                <GoalsChartContainer
                    chartEntities={chartEntities}
                    range={range}
                    chartRangeType={chartRangeType}
                    onIncrement={onIncrement}
                    onDecrement={onDecrement}
                    onChangeType={onChangeType}
                />
            )}
        </FlexCol>
    );
};

export default GoalAccountDetailChartView;
